import { useEffect, useMemo, useRef, useState } from 'react';
import type { AppSnapshot } from '../workbench-shared';
import { readWorkspaceDraft,removeWorkspaceDraft,snapshotSignature,writeWorkspaceDraft,type WorkspaceDraft } from './workspace-draft';

export function useWorkspaceDraft(account:string,enabled:boolean,current:AppSnapshot,saved:AppSnapshot,dirty:boolean,version:string){
  const [loaded,setLoaded]=useState(!enabled);
  const [recovery,setRecovery]=useState<{draft:WorkspaceDraft;cloudChanged:boolean;catalogChanged:boolean}>();
  const [stagedVersion,setStagedVersion]=useState<string>();
  const [error,setError]=useState<string>();
  const queue=useRef<Promise<unknown>>(Promise.resolve());
  const ownedDraft=useRef<string|undefined>(undefined);
  const currentVersion=useRef(version);currentVersion.current=version;
  const base=useMemo(()=>({snapshot:saved,signature:undefined as Promise<string>|undefined}),[saved.workbench,saved.userPlan]);
  const signature=()=>base.signature??=snapshotSignature(base.snapshot);
  const latest=useRef({current,dirty,version,base});latest.current={current,dirty,version,base};
  const pending=useRef(false);pending.current=Boolean(recovery)||!loaded;
  const stage=()=>{
    if(!enabled||pending.current||!latest.current.dirty)return;
    const snapshot=latest.current;
    const id=crypto.randomUUID();
    queue.current=queue.current.catch(()=>{}).then(async()=>{
      const baseSignature=await (snapshot.base.signature??=snapshotSignature(snapshot.base.snapshot));
      await writeWorkspaceDraft({account,id,savedAt:new Date().toISOString(),catalogVersion:snapshot.current.workbench.version,baseSignature,value:snapshot.current});
      ownedDraft.current=id;
      if(currentVersion.current===snapshot.version){setStagedVersion(snapshot.version);setError(undefined);}
    }).catch(()=>setError('无法写入本机草稿，请保持页面打开并尽快同步'));
  };
  useEffect(()=>{
    if(!enabled)return;
    let active=true;
    void readWorkspaceDraft(account).then(async draft=>{
      if(!draft||!active)return;
      const currentSignature=await signature();
      if(!active)return;
      if(await snapshotSignature(draft.value)===currentSignature){await removeWorkspaceDraft(account,draft.id);return;}
      setRecovery({draft,cloudChanged:draft.baseSignature!==currentSignature,catalogChanged:draft.catalogVersion!==saved.workbench.version});
    }).catch(()=>{if(active)setError('本机草稿存储不可用，请留意云端保存状态');}).finally(()=>{if(active)setLoaded(true);});
    return()=>{active=false;};
  },[account,enabled]);
  useEffect(()=>{
    if(!enabled||!loaded||recovery)return;
    if(!dirty){queue.current=queue.current.catch(()=>{}).then(async()=>{const id=ownedDraft.current;if(id)await removeWorkspaceDraft(account,id);if(ownedDraft.current===id)ownedDraft.current=undefined;}).catch(()=>{});setStagedVersion(undefined);return;}
    const timer=setTimeout(stage,200);
    return()=>clearTimeout(timer);
  },[enabled,loaded,recovery,dirty,version]);
  useEffect(()=>{
    if(!enabled)return;
    const flush=()=>stage();
    const visibility=()=>{if(document.visibilityState==='hidden')flush();};
    addEventListener('pagehide',flush);document.addEventListener('visibilitychange',visibility);
    return()=>{removeEventListener('pagehide',flush);document.removeEventListener('visibilitychange',visibility);};
  },[enabled,account]);
  const discard=async()=>{if(recovery)await removeWorkspaceDraft(account,recovery.draft.id);setRecovery(undefined);};
  return {loading:!loaded,recovery,error,locallySaved:stagedVersion===version,stage,discard,accept:()=>{ownedDraft.current=recovery?.draft.id;setRecovery(undefined);}};
}
